import axios, { AxiosInstance, AxiosResponse } from "axios";
import Vue from "vue";
import createAuthRefreshInterceptor from "axios-auth-refresh";
import { Err, InternalErr, DisplayableErr } from "@/ts/objects/Err";
import log from "loglevel";
import { ProjectRepository, ProjectRepositoryImpl } from "@/ts/repositories/ProjectRepository";
import { ProjectRepositoryMock } from "@/test-tools/mocks/ProjectRepositoryMock";
import { UserRepository, UserRepositoryImpl } from "@/ts/repositories/UserRepository";
import { UserRepositoryMock } from "@/test-tools/mocks/UserRepositoryMock";
import { Quarter } from "@/ts/objects/noneditable/value/Quarter";
import { DebugConfig } from "@/ts/InitializeApp";
import { AuthService } from "@/ts/AuthService";
import { SolanRepository, SolanRepositoryImpl } from "@/ts/repositories/SolanRepository";
import { SolanRepositoryMock } from "@/test-tools/mocks/SolanRepositoryMock";
import { LogRepository, LogRepositoryImpl } from "@/ts/repositories/LogRepository";
import { ActivityRepository, ActivityRepositoryImpl } from "@/ts/repositories/ActivityRepository";
import { ActivityRepositoryMock } from "@/test-tools/mocks/ActivityRepositoryMock";
import { LogRepositoryMock } from "@/test-tools/mocks/LogRepositoryMock";
import { CurriculumRepository, CurriculumRepositoryImpl } from "@/ts/repositories/CurriculumRepository";
import { CurriculumRepositoryMock } from "@/test-tools/mocks/CurriculumRepositoryMock";

export function initializeRepositories(
  authService: AuthService
): [UserRepository, ActivityRepository, ProjectRepository, SolanRepository, CurriculumRepository, LogRepository] {
  const userRepository = new UserRepositoryImpl(
    `https://${window.location.host}/api/user`,
    undefined,
    getAxiosInstance(authService)
  );
  const activityRepository = new ActivityRepositoryImpl(
    `https://${window.location.host}/api/activity`,
    undefined,
    getAxiosInstance(authService)
  );
  const projectRepository = new ProjectRepositoryImpl(
    `https://${window.location.host}/api/project`,
    undefined,
    getAxiosInstance(authService)
  );
  const solanRepository = new SolanRepositoryImpl(
    `https://${window.location.host}/api/solan`,
    undefined,
    getAxiosInstance(authService)
  );
  const curriculumRepository = new CurriculumRepositoryImpl(
    `https://${window.location.host}/api/curriculum`,
    undefined,
    getAxiosInstance(authService)
  );
  const logRepository = new LogRepositoryImpl(
    `https://${window.location.host}/api/log`,
    undefined,
    getAxiosInstance(authService)
  );
  return [userRepository, activityRepository, projectRepository, solanRepository, curriculumRepository, logRepository];
}

export function initializeRepositoryMocks(
  debugConfig: DebugConfig
): [UserRepository, ActivityRepository, ProjectRepository, SolanRepository, CurriculumRepository, LogRepository] {
  const userRepository = new UserRepositoryMock(
    debugConfig.debugUserId,
    debugConfig.debugUserType,
    new Quarter(2000, 1)
  );
  const activityRepository = new ActivityRepositoryMock();
  const projectRepository = new ProjectRepositoryMock();
  const solanRepository = new SolanRepositoryMock();
  const curriculumRepository = new CurriculumRepositoryMock();
  const logRepository = new LogRepositoryMock();
  return [userRepository, activityRepository, projectRepository, solanRepository, curriculumRepository, logRepository];
}

function getAxiosInstance(authService: AuthService): AxiosInstance {
  const instance = axios.create();
  instance.defaults.timeout = 15000;
  instance.defaults.withCredentials = false;
  instance.interceptors.request.use(
    function(config) {
      const idToken = Vue.prototype.$googleIdToken;
      if (idToken !== null) {
        config.headers["Authorization"] = `Bearer ${idToken}`;
      }
      return config;
    },
    function(error) {
      return Promise.reject(error);
    }
  );
  createAuthRefreshInterceptor(
    instance,
    async (): Promise<any> => {
      log.debug("createAuthRefreshInterceptor:refreshAuthCall");
      await authService.signInToGoogle(true);
      return Promise.resolve();
    }
  );
  return instance;
}

/**
 * axiosリクエストを実行する。
 *
 * @param func 実行すべきaxiosリクエスト。
 */
export async function doReq<T>(func: () => Promise<AxiosResponse<T>>): Promise<Err | T> {
  try {
    const resp = await func();
    if (resp.status < 100 || 300 <= resp.status)
      return new InternalErr(`Error response from service: ${resp.status}: ${resp.statusText}`);
    log.debug(`Successful response from service: ${resp.status}, ${JSON.stringify(resp.data)}`);
    return resp.data;
  } catch (e) {
    // TODO ちゃんと400とか500エラーをキャッチできるか？
    const errorString = JSON.stringify(e);
    log.debug(`Error from axios: ${errorString}`);
    log.error(`Error from server: ${JSON.stringify(e.response)}`);
    return new DisplayableErr(errorString, e.response?.data?.message ?? "エラーが発生しました。");
  }
}
